import { Injectable, inject } from '@angular/core';
import { CollectionRequestBuilder } from '@web/common';
import { Guid, isNil, Nillable, valueOrDefault } from '@web/utils';
import { catchError, map, Observable, of } from 'rxjs';
import { SelectItem } from '@web/shared/ui';
import { AdminSubscriptionApiService } from '../api/services/admin-subscription-api.service';
import {
  ShippingMethodProperty,
  ShippingMethodPropertyMetadata,
} from '@web/shared/module/shop';
import { AdminSubscriptionShippingMethodCollectionItemResponse } from '../api/responses/admin-subscription-shipping-method-collection-item.response';
import { AdminSubscriptionShippingMethodCollectionItem } from '../models/admin-subscription-shipping-method-collection-item';

@Injectable({
  providedIn: 'root',
})
export class AdminSubscriptionShippingMethodSelectItemsProviderService {
  private readonly subscriptionApi = inject(AdminSubscriptionApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);

  public fetchSelectItems<
    Key extends keyof AdminSubscriptionShippingMethodCollectionItemResponse,
  >(params: {
    subscriptionId: Guid;
    key: Key;
    countryId?: Nillable<Guid>;
    query?: Nillable<string>;
  }): Observable<
    SelectItem<AdminSubscriptionShippingMethodCollectionItemResponse[Key]>[]
  > {
    const { subscriptionId, key, query, countryId } = params;

    /* prettier-ignore */
    return this.subscriptionApi
      .fetchAvailableShippingMethodCollection({
        request: this.collectionRequestBuilder
          .disablePagination()
          .build(),
        countryId,
        subscriptionId,
        skipByLoading: true,
        cacheable: false,
      })
      .pipe(
        map((response) =>
          response.items
            .filter((item) => {
              if (isNil(query)) {
                return item;
              }

              return item.name.toLowerCase().includes(query);
            })
            .map((item) => new SelectItem(item.name, item[key])),
        ),
        catchError(() => of([])),
      );
  }

  public fetchShippingMethodById(params: {
    id: Guid;
    countryId?: Nillable<Guid>;
    subscriptionId: Guid;
  }): Observable<Nillable<AdminSubscriptionShippingMethodCollectionItem>> {
    const { subscriptionId, id, countryId } = params;

    return this.subscriptionApi
      .fetchAvailableShippingMethodCollection({
        request: this.collectionRequestBuilder.disablePagination().build(),
        countryId,
        subscriptionId,
        skipByLoading: true,
        cacheable: false,
      })
      .pipe(
        map((response) =>
          response.items
            .filter((item) => item.id === id)
            .map(
              (item) =>
                new AdminSubscriptionShippingMethodCollectionItem({
                  code: item.code,
                  name: item.name,
                  id: item.id,
                  description: item.description,
                  shippingPrice: item.shippingPrice,
                  totalPrice: item.totalPrice,
                  properties: item.properties.map(
                    (property) =>
                      new ShippingMethodProperty({
                        code: property.code,
                        name: property.name,
                        value: property.value,
                        metadata: valueOrDefault(property.metadata, []).map(
                          (metadata) =>
                            new ShippingMethodPropertyMetadata({
                              code: metadata.code,
                              value: metadata.value,
                            }),
                        ),
                      }),
                  ),
                }),
            ),
        ),
        map((items) => {
          const [first] = items;

          return first;
        }),
        catchError(() => of(null)),
      );
  }
}
